import type { CSSProperties, ReactNode } from 'react';

import { Badge } from '@/components/ui';
import { EquirectThumb, type EquirectKind } from '@/components/help/scenes/scene-kit';
import { cn } from '@/lib/cn';
import { ShowcaseScene } from './auth-showcase-scene';
import './auth-showcase.css';

const THUMBS: EquirectKind[] = ['living', 'kitchen', 'bedroom', 'living'];

/**
 * Right panel of the login and signup screens: the drawn living room with the
 * editor working over it (`ShowcaseScene`: a cursor picks tools and places a
 * point, a connection, a label and a polygon, on a loop) and a mini tour card
 * (thumbnails, title, "Publicado"). Decorative only, hidden from assistive
 * tech. The chip and the card are opaque-ish black, not blurred: they sit over
 * an animated layer, and a backdrop blur there would be re-sampled every
 * frame. `scene` swaps the room for a photo or another scene.
 */
export function AuthShowcase({ scene, className, style }: { scene?: ReactNode; className?: string; style?: CSSProperties }) {
  return (
    <div
      aria-hidden="true"
      data-slot="auth-showcase"
      style={style}
      className={cn('dark relative overflow-hidden rounded-card bg-black shadow-e3', className)}
    >
      <div className="absolute inset-0">{scene ?? <ShowcaseScene />}</div>
      {/* Bottom shade so the card reads on any image. */}
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />

      {/* Black chip: readable over a bright room or a dark photo alike. */}
      <div className="absolute left-5 top-5 flex items-center gap-2.5 rounded-control bg-black/70 py-1.5 pl-1.5 pr-3 text-base font-semibold tracking-tight text-white ring-1 ring-white/10">
        <span className="inline-flex size-7 items-center justify-center rounded-control bg-white/10 text-sm font-bold ring-1 ring-white/20">
          U
        </span>
        <span>
          urbania<span className="text-brand">360</span>
        </span>
      </div>

      {/* Mini tour preview (the panel is in the dark tokens). */}
      <div className="absolute bottom-6 left-6 w-[400px] max-w-[calc(100%-48px)] rounded-card bg-black/72 p-4 ring-1 ring-white/10">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-semibold text-white">Torre 1</span>
          <Badge tone="success" dot>
            Publicado
          </Badge>
        </div>
        <div className="mt-3 grid grid-cols-4 gap-2">
          {THUMBS.map((kind, i) => (
            <span
              key={i}
              // The active ring moves from the living room (1st) to Cocina (2nd) when the scene follows the door's link.
              data-follow={i === 0 ? 'from' : i === 1 ? 'to' : undefined}
              className="block overflow-hidden rounded-control ring-1 ring-white/10"
            >
              <svg viewBox="0 0 84 54" className="block h-auto w-full" preserveAspectRatio="xMidYMid slice">
                <EquirectThumb kind={kind} x={0} y={0} w={84} h={54} />
              </svg>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
